import React, { useEffect, useRef, useState } from "react";
import { Avatar, Modal, Dropdown, Menu, Grid } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { MenuOutlined } from "@ant-design/icons";
import { BiBell, BiEnvelope } from "react-icons/bi";
import { FaCog, FaHome, FaSignOutAlt } from "react-icons/fa";
import { AiOutlineSearch } from "react-icons/ai";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import mainLogo from "../../assets/images/tutify-white-logo.png";
import UserIcon from "../../assets/images/user-icon.png";
import { clearLocalStorage } from "../../helper";
import { deleteUser } from "../../redux/slice/auth/authSlice";
import {
  HOME_URL,
  STUDENT_PROFILE_SETTING_PAGE_URL,
  STUENT_JOURNEY_URL,
  TEACHER_JOURNEY_URL,
  TEACHER_PROFILE_SETTING_PAGE_URL,
  studentRouteConfig,
  teacherRouteConfig,
  About_url,
  Contact_url,
} from "../../constants";
import api, { baseUrl } from "../../services/api";
import { getWarningsForUser, markWarningAsRead } from "../../services/warning";
import { useMessages } from "../../context/chat/chat-context";
import { removeUserToken } from "../../services/notificationService";
import HeaderLogo from "../header-logo";
import Menus from "../menus";
import ResponsiveDrawer from "../Drawer/ResponsiveDrawer";
// import UserBalance from "./UserBalance";
// import NotificationBadge from "./NotificationBadge";
//import AuthModal from "./AuthModal"; // Added AuthModal import
//import styles from "./style.module.css";
import Chat from "../chat/chat";
import NotificationBadge from "../teacherHeader/NotificationBadge";
import UserBalance from "../teacherHeader/UserBalance";
import AuthModal from "../header(2)/AuthModal";

const CommonHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loggedInUser = useSelector((state) => state.auth.user);
  const { messages: allContextMessages, fetchMessages } = useMessages();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isXLargeScreen = screens.xl ;

  const [openSignUp, setOpenSignUp] = useState(false);
  const [openLogIn, setOpenLogIn] = useState(false);
  const [openModal, setOpenModal] = useState(false); // Added for AuthModal
  const [scrolling, setScrolling] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notification, setNotification] = useState([]);
  const [balanceDetails, setBalanceDetails] = useState(null);
  const [openMessageModal, setOpenMessageModal] = useState({
    itemDetail: "",
    open: false,
  });

  const collapseRef = useRef(null);
  const messagesRef = useRef(null);
  const notificationsRef = useRef(null);
  const avatarRef = useRef(null);

  dayjs.extend(relativeTime);

  // Role-based configurations
  const role = loggedInUser?.role || "guest";
  const isHomePage = location.pathname === HOME_URL;
  const routeConfig = {
    teacher: teacherRouteConfig,
    student: studentRouteConfig,
    guest: [
      { id: 1, name: "Home", link: "/" },
      {
        id: 2,
        name: "Find a Teacher",
        link: "/search?query=&gender=&modeOfTeaching=&cityId=&areaId=&course-level=&qualification-level=&education-board=&page=1",
      },
      { id: 3, name: "Blog", link: "/#BlogArticles" },
      { id: 12, name: "Contact Us", link: Contact_url },
      { id: 13, name: "About Us", link: About_url },
    ],
  }[role];

  // Role-based navigation paths
  const journeyUrl =
    role === "teacher" ? TEACHER_JOURNEY_URL : STUENT_JOURNEY_URL;
  const profileSettingsUrl =
    role === "teacher"
      ? TEACHER_PROFILE_SETTING_PAGE_URL
      : STUDENT_PROFILE_SETTING_PAGE_URL;

  // Fetch balance for authenticated users
  const fetchBalance = async () => {
    if (!loggedInUser?.id || role === "guest") return;
    try {
      const endpoint =
        role === "teacher"
          ? `/teachers/${loggedInUser.id}/balance`
          : `/students/${loggedInUser.id}/balance`;
      const res = await api.get(endpoint);
      setBalanceDetails(res?.data?.data);
    } catch (error) {
      console.error("Error fetching balance:", error);
    }
  };

  // Fetch warnings for authenticated users
  const fetchWarnings = async () => {
    if (!loggedInUser?.id || !role || role === "guest") return;
    try {
      const res = await getWarningsForUser(loggedInUser.id, role);
      setNotification(res);
    } catch (error) {
      console.error("User warning error:", error);
    }
  };

  // Mark notification as read
  const handleMarkAsRead = async (warningId) => {
    try {
      await markWarningAsRead(warningId);
      setNotification((prev) =>
        prev.map((warning) =>
          warning.id === warningId ? { ...warning, isRead: true } : warning
        )
      );
    } catch (err) {
      console.error("Error marking as read:", err);
    }
  };

  // Fetch messages for authenticated users
  useEffect(() => {
    if (loggedInUser?.id && role !== "guest") {
      fetchMessages(loggedInUser);
      fetchBalance();
      fetchWarnings();
    }
  }, [loggedInUser?.id, role, fetchMessages]);

  // Handle click outside for closing dropdowns (notifications/messages only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        messagesRef.current?.contains(event.target) ||
        notificationsRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsMessagesOpen(false);
      setIsNotificationsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle scroll for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setScrolling(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle logout
  const handleLogOut = async () => {
    const token = localStorage.getItem("fcm_token");
    if (loggedInUser && token) {
      await removeUserToken(loggedInUser, token);
    }
    clearLocalStorage();
    dispatch(deleteUser());
    navigate(HOME_URL);
  };

  // Toggle dropdowns
  const toggleNotifications = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
    setIsMessagesOpen(false);
  };

  const toggleMessages = () => {
    setIsMessagesOpen(!isMessagesOpen);
    setIsNotificationsOpen(false);
  };

  // Modal handlers
  const handleMessageModal = (status, item) => {
    setOpenMessageModal({
      open: status,
      itemDetail: {
        ...item,
        uuid: item?.user?.uuid,
        fullName: item?.user?.fullName,
      },
    });
  };

  const handleSignUpModal = (status) => {
    setOpenSignUp(status);
    setOpenLogIn(false);
    setOpenModal(status);
  };

  const handleLogInModal = (status) => {
    setOpenLogIn(status);
    setOpenSignUp(false);
    setOpenModal(status);
  };

  const handleModal = (status) => {
    setOpenModal(status);
    if (!status) {
      setOpenLogIn(false);
      setOpenSignUp(false);
    }
  };

  const handleItemClick = (itemId) => {
    if (collapseRef.current) {
      collapseRef.current.classList.remove("show");
    }
    setActiveItem(itemId);
    setDrawerOpen(false);
  };

  // Profile picture rendering
  const getProfilePictureUrl = () => {
    const localProfilePic = localStorage.getItem("profilePicture");
    if (localProfilePic) return `${baseUrl}/${localProfilePic}`;
    if (loggedInUser?.profilePicture)
      return `${baseUrl}/${loggedInUser.profilePicture}`;
    return UserIcon;
  };

  const renderProfilePicture = () => (
    <Avatar
      className="shadow-sm"
      src={<img width={30} src={getProfilePictureUrl()} alt="avatar" />}
    />
  );

  // Dropdown menu content
  const dropdownMenu = (
    <Menu>
      <Menu.Item disabled>
        <div>
          <p
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#111827",
              margin: 0,
              fontFamily: "poppins",
            }}
          >
            {loggedInUser?.fullName || "User Name"}
          </p>
          <p
            style={{
              fontSize: "12px",
              color: "#6b7280",
              margin: "2px 0 0 0",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              fontFamily: "poppins",
            }}
          >
            {loggedInUser?.email || "User Email"}
          </p>
        </div>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        key="home"
        icon={<FaHome size={18} color="#6b7280" />}
        onClick={() => navigate(journeyUrl)}
      >
        Home
      </Menu.Item>
      <Menu.Item
        key="settings"
        icon={<FaCog size={18} color="#6b7280" />}
        onClick={() => navigate(profileSettingsUrl)}
      >
        Settings
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        key="signout"
        icon={<FaSignOutAlt size={18} color="#6b7280" />}
        onClick={handleLogOut}
      >
        Sign out
      </Menu.Item>
    </Menu>
  );

  return (
    <div>
      {/* Auth Modal */}
      <AuthModal
        openModal={openModal}
        openSignUp={openSignUp}
        openLogIn={openLogIn}
        handleLogInModal={handleLogInModal}
        handleSignUpModal={handleSignUpModal}
        handleModal={handleModal}
      />

      {/* Message Modal */}
      <Modal
        maskClosable={false}
        width={700}
        footer={null}
        closable
        title=""
        open={openMessageModal.open}
        onOk={() => handleMessageModal(false, "")}
        onCancel={() => handleMessageModal(false, "")}
        zIndex={1040}
      >
        <Chat receiver={openMessageModal.itemDetail} />
      </Modal>

      {/* Mobile View */}
      <div className="d-lg-none d-flex justify-content-between align-items-center p-3">
        <MenuOutlined
          onClick={() => setDrawerOpen(true)}
          style={{ fontSize: 22 }}
        />
        {role !== "guest" ? (
          <div className="d-flex">
            {!isHomePage && balanceDetails?.balance && (
              <div className="mt-2">
                <UserBalance balance={balanceDetails.balance || 0} />
              </div>
            )}
            <div
              ref={notificationsRef}
              onClick={toggleNotifications}
              className="mt-2 ms-1"
              style={{
                padding: "4px",
                borderRadius: "50%",
                transition: "0.3s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#e6e6e6")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <NotificationBadge
                count={notification.length || 0}
                isNotificationsOpen={isNotificationsOpen}
                loggedInUser={loggedInUser}
                type="notifications"
              >
                <BiBell size={25} />
              </NotificationBadge>
            </div>
            <div
              ref={messagesRef}
              onClick={toggleMessages}
              className="me-2 ms-1 mt-2"
              style={{
                padding: "4px",
                borderRadius: "50%",
                transition: "0.3s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#e6e6e6")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <NotificationBadge
                count={allContextMessages.length}
                isMessagesOpen={isMessagesOpen}
                loggedInUser={loggedInUser}
                messages={allContextMessages}
                type="messages"
              >
                <BiEnvelope size={25} />
              </NotificationBadge>
            </div>
            <div style={{ cursor: "pointer", margin: "0.5rem 0" }}>
              <Dropdown overlay={dropdownMenu} trigger={["click"]}>
                {renderProfilePicture()}
              </Dropdown>
            </div>
          </div>
        ) : (
          <div className="d-flex align-items-center">
            <button
              onClick={() => handleLogInModal(true)}
              className="btn btn-primary btn-sm me-2 btn_contained_black"
            >
              Login
            </button>
            <button
              onClick={() => handleSignUpModal(true)}
              className="btn btn-primary btn-sm me-2 btn_contained_black"
            >
              Sign Up
            </button>
          </div>
        )}
      </div>

      {/* Drawer for Mobile */}
      <ResponsiveDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        logo={mainLogo}
        isDark={true}
        routes={routeConfig}
        activeItem={activeItem}
        onItemClick={handleItemClick}
        showAuth={role === "guest"}
        onLogin={handleLogInModal}
        onSignup={handleSignUpModal}
        userRole={role}
      />

      {/* Desktop View */}
      <div className="d-none d-lg-block">
        <div
          className="d-flex justify-content-between align-items-center pt-2"
          style={
            isHomePage ||
            (role === "teacher" &&
              !location.pathname.startsWith(TEACHER_JOURNEY_URL)) ||
            (role === "student" &&
              !location.pathname.startsWith(STUENT_JOURNEY_URL) &&
              !location.pathname.startsWith("/search")) ||
            role === "guest"
              ? {
                  padding: isXLargeScreen ? `0 110px` : `0 60px`,
                  height: "64px",
                }
              : { padding: "0px 20px", height: "64px" }
          }
        >
          {(isHomePage ||
            (role === "teacher" &&
              !location.pathname.startsWith(TEACHER_JOURNEY_URL)) ||
            (role === "student" &&
              !location.pathname.startsWith(STUENT_JOURNEY_URL) &&
              !location.pathname.startsWith("/search")) ||
            role === "guest") && (
            <div className="mt-2">
              <HeaderLogo />
            </div>
          )}
          <nav className="navbar navbar-expand-lg navbar-light">
            <div
              className="collapse navbar-collapse justify-content-start"
              ref={collapseRef}
            >
              <ul className="navbar-nav" style={{ margin: "0 0 0 20px" }}>
                {(isHomePage ||
                  (role === "teacher" &&
                    !location.pathname.startsWith(TEACHER_JOURNEY_URL)) ||
                  (role === "student" &&
                    !location.pathname.startsWith(STUENT_JOURNEY_URL) &&
                    !location.pathname.startsWith("/search")) ||
                  role === "guest") && <Menus />}
              </ul>
            </div>
          </nav>
          <div className="d-flex align-items-center">
            {role === "guest" ? (
              <>
                <button
                  onClick={() => handleLogInModal(true)}
                  className="btn btn-primary btn-sm me-2 btn_contained_black"
                >
                  Login
                </button>
                <button
                  onClick={() => handleSignUpModal(true)}
                  className="btn btn-primary btn-sm me-2 btn_contained_black"
                >
                  Sign Up
                </button>
              </>
            ) : (
              <>
                {!isHomePage && balanceDetails?.balance && (
                  <UserBalance balance={balanceDetails.balance || 0} />
                )}
                <div
                  ref={notificationsRef}
                  onClick={toggleNotifications}
                  className="ms-1"
                  style={{
                    padding: "4px",
                    borderRadius: "50%",
                    transition: "0.3s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e6e6e6")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <NotificationBadge
                    count={notification.length || 0}
                    isNotificationsOpen={isNotificationsOpen}
                    loggedInUser={loggedInUser}
                    type="notifications"
                  >
                    <BiBell size={25} />
                  </NotificationBadge>
                </div>
                <div
                  ref={messagesRef}
                  onClick={toggleMessages}
                  className="me-2 ms-1"
                  style={{
                    padding: "4px",
                    borderRadius: "50%",
                    transition: "0.3s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e6e6e6")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <NotificationBadge
                    count={allContextMessages.length}
                    isMessagesOpen={isMessagesOpen}
                    loggedInUser={loggedInUser}
                    messages={allContextMessages}
                    type="messages"
                  >
                    <BiEnvelope size={25} />
                  </NotificationBadge>
                </div>
                <div style={{ cursor: "pointer" }}>
                  <Dropdown overlay={dropdownMenu} trigger={["click"]}>
                    {renderProfilePicture()}
                  </Dropdown>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Sticky Navbar */}
        {scrolling && isHomePage && (
          <div
            className="d-flex justify-content-between align-items-center pt-2 fixed-top bg-white shadow"
            style={{
              padding: isXLargeScreen ? `0 110px` : `0 60px`,
              height: "64px",
              zIndex: 1000,
            }}
          >
            <div>
              <HeaderLogo />
            </div>
            <nav className="navbar navbar-expand-lg navbar-light">
              <div className="collapse navbar-collapse justify-content-start">
                <ul
                  className="navbar-nav"
                  style={{
                    margin: "0 0 0 20px",
                  }}
                >
                  <Menus />
                </ul>
              </div>
            </nav>

            {/* User Actions Section - Same as desktop */}
            <div className="d-flex align-items-center">
              {role === "guest" ? (
                <>
                  <button
                    onClick={() => handleLogInModal(true)}
                    className="btn btn-primary btn-sm me-2 btn_contained_black"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => handleSignUpModal(true)}
                    className="btn btn-primary btn-sm me-2 btn_contained_black"
                  >
                    Sign Up
                  </button>
                </>
              ) : (
                <>
                  {!isHomePage && balanceDetails?.balance && (
                    <UserBalance balance={balanceDetails.balance || 0} />
                  )}
                  <div
                    ref={notificationsRef}
                    onClick={toggleNotifications}
                    className="ms-1"
                    style={{
                      padding: "4px",
                      borderRadius: "50%",
                      transition: "0.3s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e6e6e6")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <NotificationBadge
                      count={notification.length || 0}
                      isNotificationsOpen={isNotificationsOpen}
                      loggedInUser={loggedInUser}
                      type="notifications"
                    >
                      <BiBell size={25} />
                    </NotificationBadge>
                  </div>
                  <div
                    ref={messagesRef}
                    onClick={toggleMessages}
                    className="me-2 ms-1"
                    style={{
                      padding: "4px",
                      borderRadius: "50%",
                      transition: "0.3s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e6e6e6")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <NotificationBadge
                      count={allContextMessages.length}
                      isMessagesOpen={isMessagesOpen}
                      loggedInUser={loggedInUser}
                      messages={allContextMessages}
                      type="messages"
                    >
                      <BiEnvelope size={25} />
                    </NotificationBadge>
                  </div>
                  <div style={{ cursor: "pointer" }}>
                    <Dropdown overlay={dropdownMenu} trigger={["click"]}>
                      {renderProfilePicture()}
                    </Dropdown>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Inline styles for sticky header consistency */}
      <style jsx>{`
        .navbar-nav {
          display: flex;
          align-items: center;
          gap: 10px; /* Adjust menu item spacing */
        }
        .navbar-nav .nav-item {
          font-size: 16px;
          font-family: "poppins", sans-serif;
        }
        .btn_contained_black {
          font-size: 12px;
          padding: 6px 12px;
        }
        .form-control {
          font-size: 12px;
          padding: 4px 8px;
        }
        .navbar-light .navbar-collapse {
          flex-grow: 1;
        }
      `}</style>
    </div>
  );
};

export default CommonHeader;
